import { IAppState } from 'config/redux/reducers';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import useSessionArea from 'services/sessionArea/hooks/useSessionArea.hook';
import { isNonEmptyArray } from 'utils/helpers.util';
import { deliverToPutAddressUpdateCart } from 'pages/Checkout/redux/actions/delivery.action';
import useValidateCart from './useValidateCart.hook';
import { findUserAddress } from 'utils/getSelectedAddress.util';
import { IAddress } from 'services/user/types/user.types';

export default function useDelivery() {
  const [isNewAddressClicked, setIsNewAddressClicked] = useState(false);
  const [selectedAddress, updateSelectedAddress] = useState(() => ({})) as any;
  const {
    userData,
    delivery: { deliverTo, validateCart },
  } = useSelector((state: IAppState) => ({
    userData: state.userData,
    delivery: state.checkout.delivery,
  }));

  const dispatch = useDispatch();

  const [sessionArea] = useSessionArea();
  const [, { onValidateCartEntriesHandler }] = useValidateCart();
  // at the time of reload we look into userv2 data address
  const addressesList = useSelector((state: IAppState) => {
    const { userV2: { addresses = [] } = { addresses: [] } } = state;
    return addresses;
  });
  const addressSelected = useMemo(() => {
    if (userData.selectedAddress) {
      return userData.selectedAddress;
    }
    return findUserAddress(addressesList, sessionArea) || {};
  }, [addressesList, userData, sessionArea]);

  const hasAddress = isNonEmptyArray(userData.addresses.list) || isNonEmptyArray(addressesList);
  const isDeliveryInformationAvailable =
    hasAddress && !userData.addresses.isLoading && !deliverTo.loading && !validateCart.loading;

  const formattedSessionArea = useMemo(
    () =>
      sessionArea
        .split('-')
        .map((title: string) => title.trim())
        .join(', '),
    [sessionArea]
  );

  const defaultAddress = useMemo(() => {
    let addressTemp = userData.addresses.list.filter(
      (address: { defaultAddress: boolean }) => address.defaultAddress
    );
    if (!addressTemp.length) {
      addressTemp = [userData.defaultAddress];
    }
    return addressTemp || [];
  }, [userData.addresses.list, userData.defaultAddress]);

  const addressChangeSuccessHandler = useCallback(
    (data: IAddress) => {
      batch(() => {
        dispatch(deliverToPutAddressUpdateCart(data) as any);
        onValidateCartEntriesHandler(data);
      });
    },
    [dispatch, onValidateCartEntriesHandler]
  );

  useEffect(() => {
    if (isDeliveryInformationAvailable && !hasAddress) {
      setIsNewAddressClicked(true);
    }
  }, [hasAddress, isDeliveryInformationAvailable]);

  useEffect(() => {
    updateSelectedAddress(userData.selectedAddress);
  }, [updateSelectedAddress, userData.selectedAddress]);

  return {
    isDeliveryInformationAvailable,
    hasAddress,
    fallbackDeliveryAddress: formattedSessionArea,
    currentlySelectedAddress: addressSelected,
    isNewAddressClicked,
    defaultAddress,
    selectedAddress,
    addressChangeSuccessHandler,
  };
}
